import { CircleCheck, CircleX, Loader2 } from 'lucide-react';
import type { UsageSheetCheck, UsageSheetCheckKind, UsageSheetCheckTarget } from '@shared/types/forge-usage-sheet';

const KIND_LABELS: Record<UsageSheetCheckKind, string> = {
  web_page: 'Lu',
  web_search: 'Cherché sur le web',
  crm_company: 'Cherché dans le CRM',
  method: 'Méthode testée',
};

const routeLabel = (route: UsageSheetCheckTarget): string =>
  route.kind === 'web_page' ? route.target.replace(/^https?:\/\//, '').slice(0, 60) : `« ${route.target} »`;

/**
 * Proof of the FDE's live checks (decision Forge FDE, F2): one line per read,
 * folded by default so the conversation stays clean. Failures are shown with
 * their plan B, never hidden.
 */
export const CheckProofLines = ({ checks, pending = [], open = false }: {
  checks: UsageSheetCheck[];
  /** Checks still running (live turn only). */
  pending?: UsageSheetCheckTarget[];
  open?: boolean;
}) => {
  if (checks.length === 0 && pending.length === 0) return null;
  const failed = checks.filter((check) => check.status === 'failed').length;
  const title = pending.length > 0
    ? `Domino vérifie (${pending.length})`
    : `Vérifications : ${checks.length - failed} confirmée${checks.length - failed > 1 ? 's' : ''}${failed > 0 ? `, ${failed} échec${failed > 1 ? 's' : ''}` : ''}`;
  return <details open={open || pending.length > 0} className="mt-2 max-w-[90%] px-1 text-xs text-domino-inkSoft">
    <summary className="cursor-pointer select-none">{title}</summary>
    <ul className="mt-1 space-y-1">
      {pending.map((route) => <li key={`${route.kind}:${route.target}`} className="flex items-start gap-1.5">
        <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
        <span>{KIND_LABELS[route.kind]} {routeLabel(route)}…</span>
      </li>)}
      {checks.map((check) => <li key={check.id} className="flex items-start gap-1.5">
        {check.status === 'ok'
          ? <CircleCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" aria-label="Vérifié" />
          : <CircleX className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-700" aria-label="Échec" />}
        <span>
          {check.firstAttempt && <>{KIND_LABELS[check.firstAttempt.kind]} {routeLabel(check.firstAttempt)} : {check.firstAttempt.summary} Autre route : </>}
          {KIND_LABELS[check.kind]} {routeLabel(check)}{check.usedFallback && !check.firstAttempt ? ' (autre route)' : ''} : {check.summary}
          {check.sourceUrl && <> · <a className="underline" href={check.sourceUrl} target="_blank" rel="noreferrer">source</a></>}
        </span>
      </li>)}
    </ul>
  </details>;
};
